import { Children, isValidElement, type PropsWithChildren, type ReactElement, useMemo } from 'react';

import type { GroupBase, MenuListProps } from 'react-select';

import Group from '#select/components/group.component.tsx';
import { flattenChildren } from '#select/utilities/flatten-children.util';

/**
 * Custom hook that prepares configuration for a virtualized select menu.
 *
 * This hook flattens the provided React children, handling both grouped and non-grouped options,
 * and returns a configuration object containing the estimated row size and the flattened children array.
 *
 * @template Option - The type of the option objects.
 * @template IsMulti - Boolean indicating if multiple selections are allowed.
 * @template Group - The type of the group objects, extending GroupBase<Option>.
 *
 * @param props - An object containing the `children` prop, which represents the menu options or groups.
 * @returns An object with:
 * - `estimatedSize`: The estimated height of each row in the virtualized list (in pixels).
 * - `flattenedChildren`: An array of React elements representing the flattened menu options.
 */
export const useVirtualSelectConfiguration = <Option, IsMulti extends boolean, Group extends GroupBase<Option>>({
  children,
}: Pick<MenuListProps<Option, IsMulti, Group>, 'children'>) => {
  const flattenedChildren = useMemo(() => {
    const flatten = Children.toArray(children);
    const first = (flatten[0] ?? {}) as ReactElement;

    if (isValidElement(first)) {
      if (first.type === Group) {
        return flattenChildren(flatten as ReactElement<PropsWithChildren<{ type: string }>>[]);
      }

      return flatten;
    }

    return [];
  }, [children]);

  return {
    estimatedSize: 40,
    flattenedChildren,
  };
};
